import React from 'react';
import { motion } from 'framer-motion';
import { Button, Chip } from '@heroui/react';
import { Clock, BookOpen, ArrowRight, GraduationCap, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Course } from '@/services/apis/courses';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/utils/cn';

interface CourseCardProps {
  course: Course;
  language?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, language = 'en' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('courses');
  const { currentLanguage } = useLanguage();

  const displayLanguage = language === 'all' ? currentLanguage : language;
  const translation = course.translations?.find((t) => t.language === displayLanguage) || course.translations?.[0];
  const name = translation?.name || 'Untitled Course';
  const description = translation?.description || '';

  const handleViewCourse = () => {
    navigate(`/courses/${course.id}`);
  };

  const guideCount = course.courseGuides?.length || 0;
  const estimatedTime =
    course.courseGuides?.reduce((total, cg) => {
      const guideTime = cg.guide?.estimatedTime || 0;
      return total + guideTime;
    }, 0) || 0;
  const estimatedTimeText = estimatedTime > 0 ? `${estimatedTime} min` : null;

  const authorName = course.author
    ? `${course.author.firstName || ''} ${course.author.lastName || ''}`.trim() || course.author.email
    : 'Unknown';

  return (
    <motion.div
      className="group relative bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200/60 dark:border-gray-800/60 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full backdrop-blur-sm"
      whileHover={{ y: -8, scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500 pointer-events-none" />

      {/* Header with modern gradient */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
        <div className="relative p-6 pb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-2">
              <h3 title={name} className="text-2xl font-bold mb-3 line-clamp-2 leading-tight text-white drop-shadow-sm">
                {name}
              </h3>
              {description && (
                <p title={description} className="text-white/85 text-sm line-clamp-2 leading-relaxed font-medium">
                  {description}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {course.isRecommended && (
              <Chip
                size="sm"
                variant="flat"
                className="border backdrop-blur-md bg-white/25 text-white font-semibold shadow-lg border-white/30"
              >
                {t('card.recommended')}
              </Chip>
            )}
            {course.categories && course.categories.length > 0 && (
              <Chip
                size="sm"
                variant="flat"
                className="border backdrop-blur-md bg-white/25 text-white font-semibold shadow-lg border-white/30"
              >
                {typeof course.categories[0] === 'object' ? course.categories[0].name : course.categories[0]}
              </Chip>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-950/50">
        {/* Stats */}
        <div className="flex items-center gap-6 mb-5 text-sm">
          {estimatedTimeText && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-950/50">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-semibold">{estimatedTimeText}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-950/50">
              <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="font-semibold">
              {guideCount} {guideCount === 1 ? t('card.guide') : t('card.guides')}
            </span>
          </div>
        </div>

        {/* Author */}
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-950/50">
            <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <span className="font-medium">
            {t('card.by')} {authorName}
          </span>
        </div>

        {/* CTA Button */}
        <Button
          color="primary"
          variant="solid"
          className={cn(
            'w-full mt-auto h-12 font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300',
            'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600',
            'hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700',
            'text-white border-0',
          )}
          endContent={<ArrowRight className="w-5 h-5" />}
          onPress={handleViewCourse}
        >
          {t('card.viewCourse')}
        </Button>
      </div>
    </motion.div>
  );
};

export default CourseCard;
